import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "courseWrapper",
    "knowledgeLevelSelect",
    "educationLevelSelect",
    "educationNetworkWrapper",
    "semesterWrapper",
    "shiftWrapper",
    "statusSelect",
    "datesWrapper",
  ];

  static values = {
    validationClasses: Boolean,
  };

  connect() {
    document.addEventListener("cocoon:before-remove", this.beforeRemove);
    this.setHiddenInputs();
    this.validateAll();
  }

  disconnect() {
    document.removeEventListener("cocoon:before-remove", this.beforeRemove);
  }

  // FORM HANDLES
  handleFormChange() {
    this.validateAll();
    this.dispatch("change", { detail: { target: this.element } });
  }

  handleMarkForDestroy(event) {
    // evita duplo clique
    event.preventDefault();

    const wrapper = event.target.closest(".nested-fields");

    // seta _destroy = 1
    const destroyField = wrapper.querySelector("input[name*='[_destroy]']");
    if (destroyField) destroyField.value = "1";

    // remove required de todos os campos
    wrapper.querySelectorAll("[required]").forEach((input) => {
      input.removeAttribute("required");
    });

    // remove qualquer validação visual aplicada por Stimulus
    wrapper
      .querySelectorAll(".is-invalid")
      .forEach((el) => el.classList.remove("is-invalid"));

    // Opção A: Esmaecer o bloco e desabilitar os inputs (preservando id e _destroy para envio no form)
    wrapper.style.opacity = "0.5";
    wrapper.style.pointerEvents = "none";
    wrapper.querySelectorAll("input, select, textarea").forEach((el) => {
      if (el.name && /\[(id|_destroy)\]$/.test(el.name)) return;
      el.disabled = true;
    });

    // Alterar o botão de remoção para estado de processamento
    const removeBtn = event.target.closest("a");
    if (removeBtn) {
      removeBtn.innerHTML = "Removendo... <div class='spinner-border spinner-border-sm text-danger' role='status'></div>";
      removeBtn.style.pointerEvents = "none";
    }
  }

  // TOGGLES
  toggleCourse() {
    const level = this.knowledgeLevelSelectTarget.value;

    if (this.knowledgeLevelSelectTarget.disabled) return; // ignorar item removido

    if (["elementary_school", "high_school"].includes(level)) {
      this.courseWrapperTarget.classList.add("d-none");
    } else {
      this.courseWrapperTarget.classList.remove("d-none");
    }
  }

  toggleEducationNetwork() {
    const level = this.knowledgeLevelSelectTarget.value;

    if (this.knowledgeLevelSelectTarget.disabled) return;

    if (level === "technical_graduation") {
      this.educationNetworkWrapperTarget.classList.remove("d-none");

      if (
        !this.educationNetworkWrapperTarget
          .querySelector(".form-group")
          .contains(this.hiddenInputs.educationNetwork)
      ) {
        this.educationNetworkWrapperTarget
          .querySelector(".form-group")
          .appendChild(this.hiddenInputs.educationNetwork);
      }
    } else {
      this.educationNetworkWrapperTarget.classList.add("d-none");

      if (this.hiddenInputs.educationNetwork.parentNode) {
        this.hiddenInputs.educationNetwork.parentNode.removeChild(
          this.hiddenInputs.educationNetwork
        );
      }
    }
  }

  toggleSemester() {
    const level = this.knowledgeLevelSelectTarget.value;

    if (this.knowledgeLevelSelectTarget.disabled) return;

    if (level === "higher_education") {
      this.semesterWrapperTarget.classList.remove("d-none");

      if (
        !this.semesterWrapperTarget
          .querySelector(".form-group")
          .contains(this.hiddenInputs.semester)
      ) {
        this.semesterWrapperTarget
          .querySelector(".form-group")
          .appendChild(this.hiddenInputs.semester);
      }
    } else {
      this.semesterWrapperTarget.classList.add("d-none");

      if (this.hiddenInputs.semester.parentNode) {
        this.hiddenInputs.semester.parentNode.removeChild(
          this.hiddenInputs.semester
        );
      }
    }
  }

  toggleCourse() {
    const level = this.knowledgeLevelSelectTarget.value;

    if (this.knowledgeLevelSelectTarget.disabled) return;

    if (["higher_education", "technical_graduation"].includes(level)) {
      this.courseWrapperTarget.classList.remove("d-none");

      if (
        !this.courseWrapperTarget
          .querySelector(".form-group")
          .contains(this.hiddenInputs.course)
      ) {
        this.courseWrapperTarget
          .querySelector(".form-group")
          .appendChild(this.hiddenInputs.course);
      }
    } else {
      this.courseWrapperTarget.classList.add("d-none");

      if (this.hiddenInputs.course.parentNode) {
        this.hiddenInputs.course.parentNode.removeChild(
          this.hiddenInputs.course
        );
      }
    }
  }

  toggleShift() {
    const statusCompleted = this.statusSelectTarget.value === "completed";
    const shiftIsRequired = this.shiftRequired && !statusCompleted;

    this.shiftWrapperTarget.querySelector(".shift-field").required =
      shiftIsRequired;

    this.shiftWrapperTarget
      .querySelector(".shift-label")
      .classList[shiftIsRequired ? "add" : "remove"]("required");

    this.shiftWrapperTarget
      .querySelector(".shift-label")
      .querySelector("abbr").style.display = shiftIsRequired ? "initial" : "none";
  }

  toggleDates() {
    const showDates = ["vocational_high_school", "higher_education"].includes(
      this.educationLevelSelectTarget.value
    );

    this.datesWrapperTargets.forEach((wrapper) => {
      wrapper.hidden = !showDates;
      wrapper.querySelectorAll("input, select").forEach((input) => {
        input.disabled = !showDates;
      });
    });
  }

  // VALIDATIONS
  validateAll() {
    this.validateKnowledgeLevel();
  }

  validateKnowledgeLevel() {
    this.toggleCourse();
    this.toggleEducationNetwork();
    this.toggleSemester();
    this.toggleShift();
    this.toggleDates();
  }

  // HELPERS
  setHiddenInputs() {
    this.shiftRequired = this.shiftWrapperTarget.querySelector(
      ".shift-field"
    ).required;

    this.hiddenInputs = {
      educationNetwork: this.educationNetworkWrapperTarget.querySelector(
        ".education-network-field"
      ),
      semester: this.semesterWrapperTarget.querySelector(".semester-field"),
      course: this.courseWrapperTarget.querySelector(".course-field"),
    };
  }

  beforeRemove = (e) => {
    const block = e.detail[0]; // bloco .nested-fields que será removido

    block.querySelectorAll("input, select, textarea").forEach((el) => {
      el.removeAttribute("required");
      el.classList.remove("is-invalid");
      if (el.name && /\[(id|_destroy)\]$/.test(el.name)) return;
      el.disabled = true;
    });
  };
}
